import { useCallback, useEffect, useRef, useState } from 'react'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Skeleton from '@mui/material/Skeleton'
import Typography from '@mui/material/Typography'
import { ThemeProvider } from '@aws-amplify/ui-react'
import { FaceLivenessDetectorCore } from '@aws-amplify/ui-react-liveness'
import { fetchAuthSession } from 'aws-amplify/auth'
import type { CredentialsAndIdentityId } from 'aws-amplify/auth'
import { useTranslation } from 'react-i18next'
import { getSelfieConfig } from '@/helpers/selfieConfig'
import { SELFIE_NAMESPACE } from '@/i18n'
import { isPlaceholderSessionId } from '@/Pages/Selfie/helpers/isPlaceholderSessionId'
import type { CameraRestartInfo } from '@/Pages/Selfie/helpers/cameraAccess'
import { useLivenessCameraGuard } from '@/Pages/Selfie/hooks/useLivenessCameraGuard'
import {
  captureVideoFrameAsDataUrl,
  findVideoElement,
  isVideoFrameReady,
} from '@/Pages/Selfie/helpers/captureVideoFrame'
import '@/Pages/Selfie/components/SelfieCaptureFeed.sass'

type AwsCredentials = CredentialsAndIdentityId['credentials']

type Props = {
  sessionId: string
  cameraReady: boolean
  onCameraReady: () => void
  onAnalysisComplete: (base64Image: string) => void
  onError: (error: unknown) => void
  onCancel: () => void
  /** A câmera travou ou falhou ao abrir e está sendo reaberta (rastro no painel). */
  onCameraRestart: (info: CameraRestartInfo) => void
}

const MOCK_CAPTURE_IMAGE =
  'data:image/jpeg;base64,/9j/4AAQSkZJRgABAQAAAQABAAD/2wBDAAgGBgcGBQgHBwcJCQgKDBQNDAsLDBkSEw8UHRofHh0aHBwgJC4nICIsIxwcKDcpLDAxNDQ0Hyc5PTgyPC4zNDL/2wBDAQkJCQwLDBgNDRgyIRwhMjIyMjIyMjIyMjIyMjIyMjIyMjIyMjIyMjIyMjIyMjIyMjIyMjIyMjIyMjIyMjIyMjL/wAARCAABAAEDASIAAhEBAxEB/8QAFQABAQAAAAAAAAAAAAAAAAAAAAn/xAAUEAEAAAAAAAAAAAAAAAAAAAAA/8QAFQEBAQAAAAAAAAAAAAAAAAAAAAX/xAAUEQEAAAAAAAAAAAAAAAAAAAAA/9oADAMBAAIRAxEAPwCwAA8A/9k='

function attachVideoReadyListener(
  video: HTMLVideoElement,
  onReady: () => void,
): () => void {
  const notifyIfReady = () => {
    if (!isVideoFrameReady(video)) return
    video.removeEventListener('loadeddata', notifyIfReady)
    video.removeEventListener('playing', notifyIfReady)
    video.removeEventListener('resize', notifyIfReady)
    onReady()
  }

  notifyIfReady()

  video.addEventListener('loadeddata', notifyIfReady)
  video.addEventListener('playing', notifyIfReady)
  video.addEventListener('resize', notifyIfReady)

  return () => {
    video.removeEventListener('loadeddata', notifyIfReady)
    video.removeEventListener('playing', notifyIfReady)
    video.removeEventListener('resize', notifyIfReady)
  }
}

export const SelfieCaptureFeed = ({
  sessionId,
  cameraReady,
  onCameraReady,
  onAnalysisComplete,
  onError,
  onCancel,
  onCameraRestart,
}: Props) => {
  const { t } = useTranslation(SELFIE_NAMESPACE)
  const visualTips = [
    t('visualTips.lighting'),
    t('visualTips.noAccessories'),
    t('visualTips.lookAtCamera'),
  ] as const
  const containerRef = useRef<HTMLDivElement>(null)
  const onCameraReadyRef = useRef(onCameraReady)
  const onAnalysisCompleteRef = useRef(onAnalysisComplete)
  const onErrorRef = useRef(onError)
  onCameraReadyRef.current = onCameraReady
  onAnalysisCompleteRef.current = onAnalysisComplete
  onErrorRef.current = onError

  const mockLiveness = getSelfieConfig().mockLiveness
  const canStartLiveness = !mockLiveness && !isPlaceholderSessionId(sessionId)
  const [detectorMounted, setDetectorMounted] = useState(false)
  const [credentials, setCredentials] = useState<AwsCredentials | null>(null)

  const camera = useLivenessCameraGuard({
    enabled: canStartLiveness,
    sessionId,
    detectorRendered: detectorMounted && credentials !== null,
    cameraReady,
    containerRef,
    onRestart: onCameraRestart,
    onFatalError: (error) => {
      onErrorRef.current(error)
    },
  })

  // Reabrir a câmera troca o <video> do detector (`key` nova): o aviso de
  // "câmera pronta" precisa se ligar ao vídeo novo, não ao da montagem anterior.
  useEffect(() => {
    if (cameraReady) return

    const container = containerRef.current
    if (!container) return

    let detachVideoListener: (() => void) | undefined
    let observer: MutationObserver | undefined

    const tryBindVideo = (): boolean => {
      const video = findVideoElement(container)
      if (!video) return false

      detachVideoListener?.()
      detachVideoListener = attachVideoReadyListener(video, () => {
        onCameraReadyRef.current()
      })
      return true
    }

    if (!tryBindVideo()) {
      observer = new MutationObserver(() => {
        if (tryBindVideo()) observer?.disconnect()
      })
      observer.observe(container, { childList: true, subtree: true })
    }

    return () => {
      observer?.disconnect()
      detachVideoListener?.()
    }
  }, [cameraReady, sessionId, camera.detectorKey])

  const handleAnalysisComplete = async () => {
    try {
      const container = containerRef.current
      if (!container) {
        throw new Error('Selfie container ref not available')
      }

      const video = findVideoElement(container)
      if (!video) {
        throw new Error('Video element not found in liveness container')
      }

      onAnalysisCompleteRef.current(captureVideoFrameAsDataUrl(video))
    } catch (error) {
      onErrorRef.current(error)
    }
  }

  const handleLivenessError = (livenessError: unknown) => {
    if (camera.handleDetectorError(livenessError)) return
    onErrorRef.current(livenessError)
  }

  useEffect(() => {
    if (!canStartLiveness) {
      setDetectorMounted(false)
      return
    }

    let cancelled = false
    let frame: number | undefined

    void fetchAuthSession()
      .then((session) => {
        if (cancelled || !session.credentials) return
        setCredentials(session.credentials as AwsCredentials)
        frame = window.requestAnimationFrame(() => {
          if (!cancelled) setDetectorMounted(true)
        })
      })
      .catch((err: unknown) => {
        if (!cancelled) onErrorRef.current(err)
      })

    return () => {
      cancelled = true
      if (frame !== undefined) window.cancelAnimationFrame(frame)
      setDetectorMounted(false)
    }
  }, [canStartLiveness, sessionId])

  const credentialProvider = useCallback(async (): Promise<AwsCredentials> => {
    const session = await fetchAuthSession()
    if (!session.credentials) throw new Error('No AWS credentials available')
    return session.credentials as AwsCredentials
  }, [])

  useEffect(() => {
    if (!mockLiveness || cameraReady) return

    const timer = window.setTimeout(() => {
      onCameraReadyRef.current()
    }, 300)

    return () => {
      window.clearTimeout(timer)
    }
  }, [mockLiveness, cameraReady, sessionId])

  const handleMockCapture = () => {
    onAnalysisCompleteRef.current(MOCK_CAPTURE_IMAGE)
  }

  if (mockLiveness) {
    return (
      <Box className="selfie-capture-feed" ref={containerRef}>
        <Box className="selfie-capture-feed__camera">
          {!cameraReady && (
            <Skeleton
              variant="rectangular"
              width="100%"
              height="100%"
              className="selfie-capture-feed__skeleton"
              aria-hidden
            />
          )}
          {cameraReady && (
            <Box className="selfie-capture-feed__mock" data-testid="selfie-mock-capture">
              <Typography className="selfie-capture-feed__mock-text">
                {t('mock.devMode')}
              </Typography>
              <Button variant="contained" onClick={handleMockCapture}>
                {t('mock.simulateCapture')}
              </Button>
            </Box>
          )}
        </Box>
        {cameraReady && (
          <Box
            component="ul"
            className="selfie-capture-feed__tips"
            aria-live="polite"
          >
            {visualTips.map((tip) => (
              <Box component="li" key={tip} className="selfie-capture-feed__tip">
                <Typography className="selfie-capture-feed__tip-text">{tip}</Typography>
              </Box>
            ))}
          </Box>
        )}
      </Box>
    )
  }

  return (
    <Box className="selfie-capture-feed" ref={containerRef}>
      <Box className="selfie-capture-feed__camera">
        {camera.canMountDetector && detectorMounted && credentials && (
          <ThemeProvider>
            <FaceLivenessDetectorCore
              key={camera.detectorKey}
              sessionId={sessionId}
              region={getSelfieConfig().region}
              config={{ credentialProvider }}
              onAnalysisComplete={handleAnalysisComplete}
              onError={handleLivenessError}
              onUserCancel={onCancel}
              disableStartScreen
              displayText={t('liveness', { ns: SELFIE_NAMESPACE, returnObjects: true }) as Record<string, string>}
            />
          </ThemeProvider>
        )}
        {!cameraReady && (
          <Skeleton
            variant="rectangular"
            width="100%"
            height="100%"
            className="selfie-capture-feed__skeleton"
            aria-hidden
          />
        )}
        {!cameraReady && camera.isRecovering && (
          <Typography
            className="selfie-capture-feed__recovering"
            role="status"
            data-testid="selfie-camera-recovering"
          >
            {t('cameraRecovering')}
          </Typography>
        )}
      </Box>
      {cameraReady && (
        <Box
          component="ul"
          className="selfie-capture-feed__tips"
          aria-live="polite"
        >
          {visualTips.map((tip) => (
            <Box component="li" key={tip} className="selfie-capture-feed__tip">
              <Typography className="selfie-capture-feed__tip-text">{tip}</Typography>
            </Box>
          ))}
        </Box>
      )}
    </Box>
  )
}
